<!-- TABLE CONTAINER -->
<div #tableContainer class="w-full min-w-0 flex-1 min-h-0 overflow-auto custom-scrollbar">
  <table
    class="w-full text-left border-separate border-spacing-0"
    [style.minWidth]="minTableWidth() === '100%' ? null : minTableWidth()"
  >
    <thead class="sticky top-0 z-10 shrink-0 shadow-xs">
      <tr class="text-text-half-dimmed text-xs font-semibold uppercase tracking-wider">
        @for (col of columns(); track col.id) {
          <th
            class="px-2 sm:px-3 py-3 select-none text-xs bg-card-bg-default border-b border-card-border-border/40 first:rounded-tl-sm last:rounded-tr-sm"
            [style.width]="col.width || null"
            [style.minWidth]="col.minWidth || null"
            [class.cursor-pointer]="col.sortable"
            [class.text-center]="col.align === 'center'"
            [class.text-right]="col.align === 'right'"
            (click)="col.sortable ? toggleSort(col.id) : null"
          >
            <span
              class="inline-flex items-center gap-1 whitespace-nowrap"
              [class.justify-center]="col.align === 'center'"
              [class.justify-end]="col.align === 'right'"
            >
              {{ col.header }}
              @if (col.tooltip) {
                <app-info-tooltip
                  [text]="col.tooltip"
                  [position]="col.tooltipPosition || 'bottom'"
                  [align]="$last || col.align === 'right' ? 'right' : 'center'"
                  size="xs"
                />
              }
              @if (sortKey() === col.id) {
                <span class="text-text-brand">{{ sortDirection() === 'asc' ? '↑' : '↓' }}</span>
              }
            </span>
          </th>
        }
      </tr>
    </thead>
    <tbody class="text-xs md:text-sm">
      @if (loading() && pagedChartRows().length === 0) {
        @for (i of skeletonRows(); track i) {
          <tr class="animate-pulse h-[52px]">
            @for (col of columns(); track col.id) {
              <td
                class="px-2 sm:px-3 py-2.5 border-b border-card-border-border/40"
                [style.width]="col.width || null"
                [style.minWidth]="col.minWidth || null"
                [class.text-center]="col.align === 'center'"
                [class.text-right]="col.align === 'right'"
              >
                <div [class]="getSkeletonClasses(col)"></div>
              </td>
            }
          </tr>
        }
      } @else if (pagedChartRows().length === 0) {
        <tr>
          <td
            [attr.colspan]="columns().length"
            class="text-center py-12 text-text-half-dimmed border-b border-card-border-border/40"
          >
            {{ emptyMessage() }}
          </td>
        </tr>
      } @else {
        @for (row of pagedChartRows(); track $index; let rowIndex = $index) {
          <tr
            (click)="onRowClick(row, $event)"
            class="hover:bg-card-bg-hover/30 transition-colors cursor-pointer h-[52px]"
          >
            @for (col of columns(); track col.id) {
              <td
                class="px-2 sm:px-3 py-2.5 border-b border-card-border-border/40"
                [style.width]="col.width || null"
                [style.minWidth]="col.minWidth || null"
                [class.overflow-visible]="col.type === 'custom'"
                [class.text-center]="col.align === 'center'"
                [class.text-right]="col.align === 'right'"
                [class.tabular-nums]="col.type === 'number' || col.type === 'rank'"
                [class.font-semibold]="col.type === 'rank'"
                [class.font-medium]="col.type === 'number'"
                [class.text-text-primary]="col.type !== 'text' || !col.align"
                [class.text-text-half-dimmed]="
                  col.type === 'text' && col.align === 'left' && col.id !== 'title'
                "
                [class.truncate]="col.type === 'text'"
              >
                @if (col.type === 'custom' && effectiveCustomCellTemplate()) {
                  <ng-container
                    *ngTemplateOutlet="
                      effectiveCustomCellTemplate();
                      context: { $implicit: row, column: col, rowIndex: rowIndex }
                    "
                  />
                } @else {
                  @switch (col.type) {
                    @case ('rank') {
                      <div class="flex items-center gap-1.5">
                        <span class="font-semibold text-text-primary"
                          >#{{ getRank(row, col) }}</span
                        >
                        @if (getMovement(row, col); as mov) {
                          <span [class]="mov.cssClass">{{ mov.label }}</span>
                        }
                      </div>
                    }
                    @case ('title') {
                      <div class="font-bold text-text-primary leading-snug truncate">
                        {{ getTitle(row, col) }}
                      </div>
                    }
                    @case ('artists') {
                      <div class="text-text-primary truncate">
                        @for (artist of getArtists(row, col); track $index; let isLast = $last) {
                          @if (artist.slug || artist.id) {
                            <button
                              type="button"
                              (click)="onArtistClick(artist, $event)"
                              class="text-text-brand hover:underline cursor-pointer p-0 m-0 border-0 bg-transparent font-inherit text-left underline-offset-2"
                            >
                              {{ artist.name }}
                            </button>
                          } @else {
                            <span>{{ artist.name }}</span>
                          }
                          @if (!isLast) {
                            <span>, </span>
                          }
                        }
                      </div>
                    }
                    @default {
                      {{ getCellValue(row, col) }}
                    }
                  }
                }
              </td>
            }
          </tr>
        }
      }
    </tbody>
  </table>
</div>

<!-- FOOTER PAGINATION ROW -->
<div
  class="flex flex-wrap items-center justify-between gap-4 shrink-0 pt-1 text-xs text-text-half-dimmed"
>
  <!-- Left: Total items range & Page size selector -->
  <div class="flex items-center gap-3 flex-wrap">
    <div>
      Showing <span class="font-semibold text-text-primary">{{ pageStartItem() }}</span> -
      <span class="font-semibold text-text-primary">{{ pageEndItem() }}</span> of
      <span class="font-semibold text-text-primary">{{ filteredSongsCount() }}</span>
      {{ itemCountLabel() }}
    </div>

    @if (showPageSizeSelector() && effectivePageSizeOptions().length > 1) {
      <div class="relative inline-flex items-center gap-1.5">
        <span class="text-card-border-border">|</span>
        <span class="text-text-half-dimmed">Show</span>
        <div class="relative inline-block text-left select-none">
          <button
            type="button"
            (click)="togglePageSizeDropdown($event)"
            class="inline-flex items-center justify-between gap-1.5 px-2 py-0.5 text-xs font-semibold rounded-md bg-card-bg-default border border-card-border-border hover:border-button-outlined-border-hover text-text-primary transition-colors cursor-pointer select-none"
            [title]="'Change rows per page'"
          >
            <span>{{ effectivePageSize() }}</span>
            <svg
              class="w-3.5 h-3.5 text-text-half-dimmed transition-transform duration-200"
              [class.rotate-180]="pageSizeDropdownOpen()"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          @if (pageSizeDropdownOpen()) {
            <div
              (click)="$event.stopPropagation()"
              class="absolute bottom-full mb-1.5 left-0 z-50 min-w-[76px] rounded-lg bg-combobox-panel-bg border border-combobox-panel-border p-1 shadow-2xl focus:outline-none flex flex-col gap-0.5"
            >
              @for (size of effectivePageSizeOptions(); track size) {
                <button
                  type="button"
                  (click)="setPageSize(size)"
                  class="w-full flex items-center justify-between px-2.5 py-1 text-xs rounded-md transition-colors cursor-pointer text-left"
                  [class.bg-combobox-list-item-hover-bg]="effectivePageSize() === size"
                  [class.text-text-primary]="effectivePageSize() === size"
                  [class.font-bold]="effectivePageSize() === size"
                  [class.text-text-half-dimmed]="effectivePageSize() !== size"
                  [class.hover:text-text-primary]="effectivePageSize() !== size"
                  [class.hover:bg-combobox-list-item-hover-bg/70]="effectivePageSize() !== size"
                >
                  <span>{{ size }}</span>
                  @if (effectivePageSize() === size) {
                    <svg class="w-3 h-3 text-text-brand" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  }
                </button>
              }
            </div>
          }
        </div>
      </div>
    }
  </div>

  <!-- Right: Pagination module -->
  <div class="flex items-center gap-2">
    <button
      type="button"
      (click)="prevPage()"
      [disabled]="currentPage() <= 1"
      class="p-1.5 rounded-[5px] bg-transparent border border-button-outlined-border-default text-text-primary hover:bg-button-naked-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
      title="Previous Page"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="m15 18-6-6 6-6" />
      </svg>
    </button>

    <span>Go to</span>
    <input
      type="number"
      min="1"
      [max]="totalPages()"
      [value]="currentPage()"
      (change)="onGoToInput($event)"
      (keyup.enter)="onGoToInput($event)"
      class="w-12 h-7 bg-transparent hover:bg-card-bg-hover focus:bg-card-bg-hover border border-card-border-border rounded-[5px] text-center font-bold text-text-primary focus:outline-none focus:border-button-outlined-border-hover transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
    />
    <span>of {{ totalPages() }}</span>

    <button
      type="button"
      (click)="nextPage()"
      [disabled]="currentPage() >= totalPages()"
      class="p-1.5 rounded-[5px] bg-transparent border border-button-outlined-border-default text-text-primary hover:bg-button-naked-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
      title="Next Page"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    </button>
  </div>
</div>
